// src/containers/FormBuilderDashboard/components/FormEditor.jsx
import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Box, Button, Typography, Stack, Alert, IconButton, TextField, 
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogContentText, 
  DialogActions, Paper, FormControlLabel, Checkbox 
} from '@mui/material';
import { ArrowBack, Save, Add, Sync, Palette } from '@mui/icons-material';
import HelpTooltip from '../../../components/HelpTooltip';
import SectionEditor from './SectionEditor';
import FormTotalingTabs from './FormTotalingTabs';
import AddSectionModal from './AddSectionModal';
import FormAppearanceSettings from './FormAppearanceSettings';
import { updateAllMpiWorkItems } from '../../../actions/apiWorkItems';

const FormEditor = ({
  editingForm,
  setEditingForm,
  formName,
  setFormName,
  sections,
  setSections,
  headers,
  setHeaders,
  formTotaling,
  setFormTotaling,
  sendSftp,          // ← Received from parent
  setSendSftp,       // ← Received from parent
  error,
  tableRefs,
  handleBack,
  handleSave,
  openAddSectionModal,
  sectionModalOpen,
  newSectionName,
  setNewSectionName,
  closeAddSectionModal,
  handleAddSection,
  saving,
  isDataReady,
  formKey,
}) => {
  const sectionOrder = useMemo(() => Object.values(sections), [sections]);
  const work_item_presets = useSelector(state => state.settings_refresh?.work_item_presets || []);
  const default_profiles = useSelector(state => state.settings_users?.default_profiles || []).filter(
    profile => !['admin', 'super_admin'].includes(profile.name.toLowerCase())
  );

  const [updatePresetsOpen, setUpdatePresetsOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const dispatch = useDispatch();

  const currentDisplayName = editingForm?.form_settings?.displayName || formName || '';

  const handleUpdateAllPresets = async () => {
    setIsUpdating(true);
    setUpdateError(null);
    setUpdateSuccess(false);
    try {
      await dispatch(updateAllMpiWorkItems());
      setUpdateSuccess(true);
      setTimeout(() => setUpdatePresetsOpen(false), 2000);
    } catch (err) {
      setUpdateError('Failed to update all presets. Please try again.');
      console.error('Update all presets error:', err);
    } finally {
      setIsUpdating(false);
    }
  };

  const updateDisplayName = (value) => {
    setEditingForm(prev => ({
      ...prev,
      form_settings: {
        ...(prev?.form_settings || {}),
        displayName: value,
      },
    }));
  };

  return (
    <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {editingForm ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Top Bar */}
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', bgcolor: 'background.paper', zIndex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <IconButton onClick={handleBack}>
                <ArrowBack />
              </IconButton>

              <Stack direction="row" alignItems="center" spacing={2} sx={{ flex: 1 }}>
                <TextField
                  label="Form Name (Internal)"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{ width: 280 }}
                  helperText="Used in tabs and admin"
                />
                <TextField
                  label="Display Name"
                  value={currentDisplayName}
                  onChange={(e) => updateDisplayName(e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{ width: 280 }}
                  helperText="Shown on printed report"
                />

                {/* Send to iPacket (SFTP) Checkbox */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!sendSftp}                    // Safe guard
                      onChange={(e) => setSendSftp(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Send to iPacket (SFTP)"
                  sx={{ ml: 2 }}
                />

                <Button
                  variant="outlined"
                  startIcon={<Palette />}
                  onClick={() => setAppearanceOpen(true)}
                >
                  Form Appearance
                </Button>
              </Stack>

              <Stack direction="row" alignItems="center" spacing={1}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Form'}
                </Button>

                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<Sync />}
                  onClick={() => setUpdatePresetsOpen(true)}
                >
                  Update All Vehicle Presets
                </Button>
              </Stack>
            </Stack>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">Sections</Typography>
              <Stack direction="row" alignItems="center">
                <Button startIcon={<Add />} onClick={openAddSectionModal}>
                  Add Section
                </Button>
                <HelpTooltip title="Create a new section." />
              </Stack>
            </Stack>

            <SectionEditor
              sectionOrder={sectionOrder}
              sections={sections}
              setSections={setSections}
              headers={headers}
              setHeaders={setHeaders}
              tableRefs={tableRefs}
              work_item_presets={work_item_presets}
              isDataReady={isDataReady}
              formKey={formKey}
              default_profiles={default_profiles}
            />

            <FormTotalingTabs
              formTotaling={formTotaling}
              setFormTotaling={setFormTotaling}
            />
          </Box>

          <AddSectionModal
            open={sectionModalOpen}
            newSectionName={newSectionName}
            setNewSectionName={setNewSectionName}
            onClose={closeAddSectionModal}
            onAdd={handleAddSection}
          />
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Paper sx={{ p: 8, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="h6">Select a form to edit</Typography>
            <Typography variant="body2">or click "New" in the Forms panel</Typography>
          </Paper>
        </Box>
      )}

      {/* Update All Vehicle Presets Confirmation Modal */}
      <Dialog open={updatePresetsOpen} onClose={() => setUpdatePresetsOpen(false)} disableEscapeKeyDown={isUpdating}>
        <DialogTitle>Update All Vehicle Presets?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This process will update presets across all vehicles and may take about 2 minutes to complete.
            <br /><br />
            Are you sure you want to proceed?
          </DialogContentText>
          {updateSuccess && <Alert severity="success" sx={{ mt: 2 }}>Saved successfully!</Alert>}
          {updateError && <Alert severity="error" sx={{ mt: 2 }}>{updateError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdatePresetsOpen(false)} disabled={isUpdating}>Cancel</Button>
          <Button 
            onClick={handleUpdateAllPresets} 
            variant="contained" 
            color="primary"
            disabled={isUpdating}
            startIcon={isUpdating ? <CircularProgress size={20} /> : null}
          >
            {isUpdating ? 'Updating...' : 'Yes, Update All'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Form Appearance Modal */}
      <FormAppearanceSettings
        open={appearanceOpen}
        onClose={() => setAppearanceOpen(false)}
        editingForm={editingForm}
        setEditingForm={setEditingForm}
      />
    </Box>
  );
};

export default FormEditor;